import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { KeyboardEvent } from 'react';
import { twMerge } from 'tailwind-merge';

export interface QuerySearchProps {
  /** Current search value */
  value: string;
  /** Callback when search value changes */
  onChange: (value: string) => void;
  /** Optional className for styling */
  className?: string;
}

/**
 * QuerySearch - Search input for filtering queries in sidebar
 *
 * Features:
 * - Magnifying glass icon (leading)
 * - Clear button when text present
 * - Full accessibility (searchbox role, ARIA attributes)
 * - Controlled input
 *
 * Based on SearchBar.tsx pattern but simplified for sidebar context.
 *
 * @example
 * ```tsx
 * const [search, setSearch] = useState('');
 * <QuerySearch value={search} onChange={setSearch} />
 * ```
 */
export const QuerySearch = ({
  value,
  onChange,
  className,
}: QuerySearchProps) => {
  const handleClear = () => {
    onChange('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      onChange('');
    }
  };

  return (
    <div className={twMerge('relative w-full', className)}>
      {/* Leading icon */}
      <div className="absolute ml-3 flex h-full items-center justify-center">
        <MagnifyingGlassIcon
          className="text-chariot-text-secondary size-5"
          data-testid="query-search-icon"
        />
      </div>

      {/* Search input */}
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search queries"
        role="searchbox"
        aria-label="Search queries"
        aria-autocomplete="list"
        className={twMerge(
          'bg-chariot-transparent text-chariot-text-primary w-full rounded-md border-0 py-2 pr-10 pl-10 text-sm',
          'placeholder:text-chariot-text-secondary',
          'focus:ring-chariot-brand-primary focus:ring-2 focus:outline-none'
        )}
        data-testid="query-search-input"
      />

      {/* Trailing clear button (only shown when value exists) */}
      {value && (
        <button
          onClick={handleClear}
          className="absolute top-0 right-3 flex h-full items-center justify-center"
          aria-label="Clear search"
          data-testid="query-search-clear"
        >
          <XMarkIcon className="text-chariot-text-primary hover:text-chariot-text-primary size-5 transition-colors" />
        </button>
      )}
    </div>
  );
};
